// src/components/GreetingCard/GreetingCardCanvas.jsx

import React, { useEffect, useState } from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Rect as KonvaRect,
  Shape as KonvaShape,
  Text as KonvaText,
} from "react-konva";
import useImage from "use-image";
import EditableText from "./EditableText";
import { CARD_W as DEFAULT_W, CARD_H as DEFAULT_H } from "../../utils/geometry";

// Helper to extract dominant edge color from an image
const extractEdgeColor = (img) => {
  if (!img) return null;
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const sampleSize = 10; // pixels to sample from each edge
  
  canvas.width = img.width;
  canvas.height = img.height;
  
  try {
    ctx.drawImage(img, 0, 0);
    
    // Sample pixels from all edges
    const samples = [];
    
    // Top edge
    const topData = ctx.getImageData(0, 0, img.width, sampleSize).data;
    // Bottom edge
    const bottomData = ctx.getImageData(0, img.height - sampleSize, img.width, sampleSize).data;
    // Left edge
    const leftData = ctx.getImageData(0, 0, sampleSize, img.height).data;
    // Right edge
    const rightData = ctx.getImageData(img.width - sampleSize, 0, sampleSize, img.height).data;
    
    // Collect RGB values
    [topData, bottomData, leftData, rightData].forEach(data => {
      for (let i = 0; i < data.length; i += 4) {
        samples.push({ r: data[i], g: data[i + 1], b: data[i + 2] });
      }
    });
    
    // Calculate average color
    const avg = samples.reduce(
      (acc, c) => ({ r: acc.r + c.r, g: acc.g + c.g, b: acc.b + c.b }),
      { r: 0, g: 0, b: 0 }
    );
    const count = samples.length;
    
    return `rgb(${Math.round(avg.r / count)}, ${Math.round(avg.g / count)}, ${Math.round(avg.b / count)})`;
  } catch (e) {
    console.warn('Could not extract edge color:', e);
    return null;
  }
};

// Decorative overlay component renderer
const DecorativeOverlay = ({ type, color, imgX, imgY, imgW, imgH, scale = 1 }) => {
  if (type === 'none' || !type) return null;
  
  // Apply proportional scaling to overlay sizes
  const s = scale;
  
  const overlays = {
    'floral-corners': (
      <>
        {/* Top-left floral corner */}
        <KonvaShape
          sceneFunc={(context, shape) => {
            const x = imgX;
            const y = imgY;
            const size = Math.min(imgW, imgH) * 0.2 * s;
            
            context.beginPath();
            // Main branch
            context.moveTo(x, y + size);
            context.quadraticCurveTo(x + size * 0.3, y + size * 0.5, x + size, y);
            
            // Leaves
            for (let i = 0; i < 4; i++) {
              const t = (i + 1) / 5;
              const bx = x + size * t;
              const by = y + size * (1 - t);
              const leafSize = size * 0.15;
              
              context.moveTo(bx, by);
              context.quadraticCurveTo(bx - leafSize, by - leafSize * 1.5, bx, by - leafSize * 2);
              context.quadraticCurveTo(bx + leafSize, by - leafSize * 1.5, bx, by);
            }
            
            context.fillStrokeShape(shape);
          }}
          stroke={color}
          strokeWidth={2}
          fill="transparent"
        />
        {/* Top-right floral corner */}
        <KonvaShape
          sceneFunc={(context, shape) => {
            const x = imgX + imgW;
            const y = imgY;
            const size = Math.min(imgW, imgH) * 0.2 * s;
            
            context.beginPath();
            context.moveTo(x, y + size);
            context.quadraticCurveTo(x - size * 0.3, y + size * 0.5, x - size, y);
            
            for (let i = 0; i < 4; i++) {
              const t = (i + 1) / 5;
              const bx = x - size * t;
              const by = y + size * (1 - t);
              const leafSize = size * 0.15;
              
              context.moveTo(bx, by);
              context.quadraticCurveTo(bx + leafSize, by - leafSize * 1.5, bx, by - leafSize * 2);
              context.quadraticCurveTo(bx - leafSize, by - leafSize * 1.5, bx, by);
            }
            
            context.fillStrokeShape(shape);
          }}
          stroke={color}
          strokeWidth={2}
          fill="transparent"
        />
        {/* Bottom-left floral corner */}
        <KonvaShape
          sceneFunc={(context, shape) => {
            const x = imgX;
            const y = imgY + imgH;
            const size = Math.min(imgW, imgH) * 0.2 * s;
            
            context.beginPath();
            context.moveTo(x, y - size);
            context.quadraticCurveTo(x + size * 0.3, y - size * 0.5, x + size, y);
            
            for (let i = 0; i < 4; i++) {
              const t = (i + 1) / 5;
              const bx = x + size * t;
              const by = y - size * (1 - t);
              const leafSize = size * 0.15;
              
              context.moveTo(bx, by);
              context.quadraticCurveTo(bx - leafSize, by + leafSize * 1.5, bx, by + leafSize * 2);
              context.quadraticCurveTo(bx + leafSize, by + leafSize * 1.5, bx, by);
            }
            
            context.fillStrokeShape(shape);
          }}
          stroke={color}
          strokeWidth={2}
          fill="transparent"
        />
        {/* Bottom-right floral corner */}
        <KonvaShape
          sceneFunc={(context, shape) => {
            const x = imgX + imgW;
            const y = imgY + imgH;
            const size = Math.min(imgW, imgH) * 0.2 * s;
            
            context.beginPath();
            context.moveTo(x, y - size);
            context.quadraticCurveTo(x - size * 0.3, y - size * 0.5, x - size, y);
            
            for (let i = 0; i < 4; i++) {
              const t = (i + 1) / 5;
              const bx = x - size * t;
              const by = y - size * (1 - t);
              const leafSize = size * 0.15;
              
              context.moveTo(bx, by);
              context.quadraticCurveTo(bx + leafSize, by + leafSize * 1.5, bx, by + leafSize * 2);
              context.quadraticCurveTo(bx - leafSize, by + leafSize * 1.5, bx, by);
            }
            
            context.fillStrokeShape(shape);
          }}
          stroke={color}
          strokeWidth={2}
          fill="transparent"
        />
      </>
    ),
    
    'leaves-wreath': (
      <KonvaShape
        sceneFunc={(context, shape) => {
          const cx = imgX + imgW / 2;
          const cy = imgY + imgH / 2;
          const radiusX = imgW * 0.45;
          const radiusY = imgH * 0.45;
          const numLeaves = Math.max(12, Math.round(24 * s));
          
          for (let i = 0; i < numLeaves; i++) {
            const angle = (i / numLeaves) * Math.PI * 2;
            const x = cx + Math.cos(angle) * radiusX;
            const y = cy + Math.sin(angle) * radiusY;
            const leafAngle = angle + Math.PI / 2;
            const leafLen = Math.min(imgW, imgH) * 0.08 * s;
            
            context.beginPath();
            context.moveTo(x, y);
            context.quadraticCurveTo(
              x + Math.cos(leafAngle - 0.3) * leafLen,
              y + Math.sin(leafAngle - 0.3) * leafLen * 1.5,
              x + Math.cos(leafAngle) * leafLen * 0.5,
              y + Math.sin(leafAngle) * leafLen * 2
            );
            context.quadraticCurveTo(
              x + Math.cos(leafAngle + 0.3) * leafLen,
              y + Math.sin(leafAngle + 0.3) * leafLen * 1.5,
              x, y
            );
            context.stroke();
          }
          
          context.fillStrokeShape(shape);
        }}
        stroke={color}
        strokeWidth={1.5}
        fill="transparent"
      />
    ),
    
    'snow-splatter': (
      <>
        {/* Create random snow splatter dots around edges */}
        {Array.from({ length: Math.max(50, Math.round(100 * s)) }).map((_, i) => {
          const edge = i % 4; // 0: top, 1: right, 2: bottom, 3: left
          const progress = (i / 25) % 1;
          const depth = Math.random() * 0.18 * s + 0.03;
          const size = (Math.random() * 14 + 5) * s;
          
          let x, y;
          if (edge === 0) { // top
            x = imgX + progress * imgW;
            y = imgY + depth * imgH;
          } else if (edge === 1) { // right
            x = imgX + imgW - depth * imgW;
            y = imgY + progress * imgH;
          } else if (edge === 2) { // bottom
            x = imgX + progress * imgW;
            y = imgY + imgH - depth * imgH;
          } else { // left
            x = imgX + depth * imgW;
            y = imgY + progress * imgH;
          }
          
          return (
            <KonvaRect
              key={i}
              x={x - size / 2}
              y={y - size / 2}
              width={size}
              height={size}
              fill={color}
              cornerRadius={size / 2}
              opacity={0.4 + Math.random() * 0.4}
            />
          );
        })}
      </>
    ),
    
    'stars': (
      <>
        {/* Decorative stars in corners and edges */}
        {Array.from({ length: Math.max(20, Math.round(40 * s)) }).map((_, i) => {
          const edge = i % 4;
          const progress = ((i / 10) % 1);
          const offset = Math.random() * 0.12 * s + 0.03;
          const starSize = (Math.random() * 16 + 8) * s;
          
          let x, y;
          if (edge === 0) {
            x = imgX + progress * imgW;
            y = imgY + offset * imgH;
          } else if (edge === 1) {
            x = imgX + imgW - offset * imgW;
            y = imgY + progress * imgH;
          } else if (edge === 2) {
            x = imgX + progress * imgW;
            y = imgY + imgH - offset * imgH;
          } else {
            x = imgX + offset * imgW;
            y = imgY + progress * imgH;
          }
          
          return (
            <KonvaShape
              key={i}
              sceneFunc={(context, shape) => {
                const spikes = 4;
                const outerRadius = starSize;
                const innerRadius = starSize * 0.4;
                
                context.beginPath();
                for (let j = 0; j < spikes * 2; j++) {
                  const radius = j % 2 === 0 ? outerRadius : innerRadius;
                  const angle = (j / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
                  const sx = x + Math.cos(angle) * radius;
                  const sy = y + Math.sin(angle) * radius;
                  
                  if (j === 0) context.moveTo(sx, sy);
                  else context.lineTo(sx, sy);
                }
                context.closePath();
                context.fillStrokeShape(shape);
              }}
              fill={color}
              opacity={0.5 + Math.random() * 0.3}
            />
          );
        })}
      </>
    ),
    
    'hearts': (
      <>
        {/* Decorative hearts around edges */}
        {Array.from({ length: Math.max(8, Math.round(16 * s)) }).map((_, i) => {
          const edge = i % 4;
          const progress = ((i / 4) % 1);
          const offset = Math.random() * 0.06 * s + 0.02;
          const heartSize = (Math.random() * 10 + 6) * s;
          
          let x, y;
          if (edge === 0) {
            x = imgX + progress * imgW;
            y = imgY + offset * imgH;
          } else if (edge === 1) {
            x = imgX + imgW - offset * imgW;
            y = imgY + progress * imgH;
          } else if (edge === 2) {
            x = imgX + progress * imgW;
            y = imgY + imgH - offset * imgH;
          } else {
            x = imgX + offset * imgW;
            y = imgY + progress * imgH;
          }
          
          return (
            <KonvaShape
              key={i}
              sceneFunc={(context, shape) => {
                const s = heartSize;
                context.beginPath();
                context.moveTo(x, y + s * 0.3);
                context.bezierCurveTo(x - s * 0.5, y - s * 0.3, x - s, y + s * 0.3, x, y + s);
                context.bezierCurveTo(x + s, y + s * 0.3, x + s * 0.5, y - s * 0.3, x, y + s * 0.3);
                context.closePath();
                context.fillStrokeShape(shape);
              }}
              fill={color}
              opacity={0.4 + Math.random() * 0.3}
            />
          );
        })}
      </>
    ),
  };
  
  return overlays[type] || null;
};

export default function GreetingCardCanvas({
  side = "front",          // "front" or "inside"
  bgSrc,
  textProps,
  setText,
  cardWidth = DEFAULT_W,   // allow caller to override default 600×400 card size (3:2 ratio)
  cardHeight = DEFAULT_H,
  // Inside card styling props
  insideBorderColor,
  insideBorderWidth = 0,
  insideBorderStyle = "solid",
  // Cover/front card styling props
  coverBorderColor,
  coverBorderWidth = 0,
  coverBorderStyle = "solid",
  coverOverlay = "none",
  coverOverlayColor = "#1F2937",
  // Preview mode - skip auto-scaling (parent handles scaling via CSS transform)
  isPreview = false,
  // Fit canvas exactly to image (no padding/background)
  fitToImage = false,
  // Multiple text entries for collaboration mode (inside view only)
  // Array of { id, text, x, y, width, fontSize, fontFamily, fill, ... }
  insideTextEntries = null,
  onTextEntryChange = null, // Callback: (entryId, updatedProps) => void
}) {
  const [bg, bgStatus] = useImage(bgSrc);
  const [stageSize, setStageSize] = useState({ width: cardWidth, height: cardHeight });
  const [scale, setScale] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [edgeColor, setEdgeColor] = useState(null);
  const containerRef = React.useRef(null);

  // Handle image loading errors
  useEffect(() => {
    if (bgStatus === 'failed') {
      setImageError(true);
      console.warn('Failed to load background image:', bgSrc);
    } else if (bgStatus === 'loaded') {
      setImageError(false);
    }
  }, [bgStatus, bgSrc]);

  // Extract edge color when image loads for dynamic background fill
  useEffect(() => {
    if (bg && bgStatus === 'loaded' && side === 'front') {
      const color = extractEdgeColor(bg);
      if (color) {
        setEdgeColor(color);
      }
    }
  }, [bg, bgStatus, side]);

  // Handle responsive scaling
  useEffect(() => {
    // Determine dimensions to use for scaling
    // When fitToImage and bg is loaded, use image dimensions
    const targetWidth = fitToImage && bg ? bg.width : cardWidth;
    const targetHeight = fitToImage && bg ? bg.height : cardHeight;
    
    // In preview mode, skip auto-scaling - parent handles it via CSS transform
    if (isPreview) {
      setScale(1);
      setStageSize({ width: targetWidth, height: targetHeight });
      return;
    }

    const updateSize = () => {
      // Get parent container's available space (go up to find the constraining element)
      const container = containerRef.current;
      if (!container) return;
      
      // Get the parent's available space (the flex container with max constraints)
      const parent = container.parentElement?.parentElement;
      if (!parent) {
        setScale(1);
        setStageSize({ width: targetWidth, height: targetHeight });
        return;
      }
      
      // Get available space from the parent wrapper
      const availableWidth = parent.clientWidth - 32; // Account for padding
      const availableHeight = window.innerHeight * 0.7; // 70vh max
      
      // Guard against zero dimensions
      if (availableWidth <= 0 || availableHeight <= 0) {
        setScale(1);
        setStageSize({ width: targetWidth, height: targetHeight });
        return;
      }
      
      // Calculate the scale to fit within available space while maintaining aspect ratio
      const scaleX = availableWidth / targetWidth;
      const scaleY = availableHeight / targetHeight;
      // Use the smaller scale to ensure image fits without cropping
      const newScale = Math.min(scaleX, scaleY, 1.5);
      
      // Ensure scale is valid (not NaN, not zero)
      const safeScale = (newScale && newScale > 0) ? newScale : 1;
      
      setScale(safeScale);
      // Size stage to exactly fit the scaled image (maintains aspect ratio)
      setStageSize({
        width: targetWidth * safeScale,
        height: targetHeight * safeScale
      });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [cardWidth, cardHeight, isPreview, fitToImage, bg]);

  // Determine effective canvas dimensions based on fitToImage mode
  // When fitToImage is true, canvas sizes to image (no padding)
  // When false, image scales to fit within cardWidth/cardHeight
  const effectiveCardWidth = fitToImage && bg ? bg.width : cardWidth;
  const effectiveCardHeight = fitToImage && bg ? bg.height : cardHeight;
  
  // preserve image aspect‐ratio
  const { w, h } = bg
    ? { w: bg.width, h: bg.height }
    : { w: effectiveCardWidth, h: effectiveCardHeight };
  
  // When fitToImage, image fills canvas exactly (scale=1, position=0,0)
  // Otherwise, scale to fit within card dimensions with centering
  const imgScale = fitToImage ? 1 : Math.min(cardWidth / w, cardHeight / h);
  const imgX = fitToImage ? 0 : (cardWidth - w * imgScale) / 2;
  const imgY = fitToImage ? 0 : (cardHeight - h * imgScale) / 2;
  
  // Calculate fill ratio - how much of the canvas the image occupies
  // Used to proportionally scale borders and overlays for smaller templates
  const imgFillRatio = Math.min(1, (w * imgScale * h * imgScale) / (cardWidth * cardHeight));
  const proportionalScale = Math.max(0.4, Math.sqrt(imgFillRatio)); // sqrt for gentler scaling, min 40%
  
  // Proportionally scaled border width for cover
  const scaledCoverBorderWidth = Math.round(coverBorderWidth * proportionalScale);

  // Build a bounding rectangle for the editable text with proper padding
  const padding = side === "front" ? 40 : 0; // Remove padding for inside text
  
  // For inside text, account for border width + some padding
  const insidePadding = insideBorderWidth > 0 ? insideBorderWidth + 16 : 16;
  
  // For front side, constrain text to image bounds plus padding
  // Use effective dimensions (which match image when fitToImage=true)
  const bounds = side === "front" ? {
    x: bg ? imgX + padding : padding,
    y: bg ? imgY : 0,
    width: bg ? w * imgScale - padding * 2 : effectiveCardWidth - padding * 2,
    height: bg ? h * imgScale : effectiveCardHeight,
  } : {
    x: insidePadding, // Account for border + padding
    y: insidePadding, // Account for border + padding
    width: effectiveCardWidth - (insidePadding * 2), // Constrain to inner area
    height: effectiveCardHeight - (insidePadding * 2), // Constrain to inner area
  };

  // -----------------------------
  // Helper: fit font size so that text height stays inside bounds
  // -----------------------------
  const fitFontSize = (text, fontFamily, startSize, maxWidth, maxHeight) => {
    // Ensure text is a string and guard against invalid inputs
    const safeText = String(text || "");
    if (!safeText || startSize <= 4) return startSize;

    // Re-use a single canvas to avoid re-creating it each render
    const canvas = fitFontSize._c || (fitFontSize._c = document.createElement("canvas"));
    const ctx    = canvas.getContext("2d");

    // Simple word-wrap algorithm to estimate number of lines at given font size
    const measureHeight = (fontSize) => {
      ctx.font = `${fontSize}px ${fontFamily}`;
      const words = safeText.split(/\s+/);
      let line   = "";
      let lines  = 1;
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxWidth) {
          lines += 1;
          line = w;
        } else {
          line = test;
        }
      }
      return lines * fontSize * 1.2; // rough lineHeight of 1.2
    };

    let fontSize = startSize;
    while (fontSize > 8 && measureHeight(fontSize) > maxHeight) {
      fontSize -= 1;
    }
    return Math.max(fontSize, 8); // Minimum font size of 8
  };

  // Ensure text is never undefined and properly positioned
  const safeTextProps = {
    text: textProps.text || "",
    wrap: "word",
    draggable: textProps.draggable ?? true,
    // Preserve all incoming properties
    ...textProps,
    // Calculate optimal text width with padding
    // For inside text, use bounds.width to respect border padding
    width: side === "front" ? bounds.width : (textProps.width || bounds.width),
    // Dynamically reduce the font size if the text would overflow the allowed area
    fontSize: (() => {
      const startSize = typeof textProps.fontSize === 'number'
        ? textProps.fontSize
        : (side === "front" ? 72 : 24);

      // Only perform auto-fit when (1) we're on the front side *and* (2) the
      // text is draggable i.e. we are inside the editor. For static previews
      // we return the stored size untouched so the cover looks identical in
      // the viewer.
      if (side === "front" && textProps.draggable !== false) {
        return fitFontSize(
          textProps.text || "",
          textProps.fontFamily || (side === "front" ? "Lilita One" : "'Inter', sans-serif"),
          startSize,
          bounds.width,
          bounds.height,
        );
      }

      return startSize;
    })(),
    fontFamily: textProps.fontFamily || (side === "front" ? "Lilita One" : "'Inter', sans-serif"),
    fill: textProps.fill || "#000000",
    align: textProps.align || (side === "front" ? "center" : "left"),
  };

  // Set initial position if not provided or if text hasn't been positioned yet
  if (typeof textProps.x !== 'number' || typeof textProps.y !== 'number') {
    // For inside text, provide a default starting position
    if (side !== "front") {
      safeTextProps.x = bounds.x;
      safeTextProps.y = bounds.y;
    } else {
      // For cover text, provide a centered default position
      // This ensures text is visible immediately while EditableText handles fine-tuning
      safeTextProps.x = bounds.x;
      safeTextProps.y = bounds.y + bounds.height * 0.3; // Start at upper third of image
    }
  } else {
    // For existing positioned text, preserve the exact coordinates
    // Only do soft clamping to prevent text from being completely off-screen
    // Allow some margin outside bounds (user might intentionally position near edges)
    const margin = 50;
    safeTextProps.x = Math.max(-margin, Math.min(effectiveCardWidth + margin, textProps.x));
    safeTextProps.y = Math.max(-margin, Math.min(effectiveCardHeight + margin, textProps.y));
  }

  // Debug logging - remove after fixing
  console.log('[GreetingCardCanvas] Render debug:', {
    side,
    bgLoaded: !!bg,
    bgStatus,
    textContent: safeTextProps.text,
    textPosition: { x: safeTextProps.x, y: safeTextProps.y },
    textFill: safeTextProps.fill,
    fontSize: safeTextProps.fontSize,
    fontFamily: safeTextProps.fontFamily,
    bounds,
    stageSize,
    scale,
    imagePosition: { x: imgX, y: imgY },
    imageSize: { w: w * imgScale, h: h * imgScale }
  });

  return (
    <div 
      ref={containerRef} 
      className="card-container"
      style={{
        // Size container to exactly match canvas
        width: stageSize.width,
        height: stageSize.height,
      }}
    >
      <Stage 
        width={stageSize.width} 
        height={stageSize.height}
        scaleX={scale}
        scaleY={scale}
        // Optimize performance and reduce unnecessary events
        listening={true}
        perfectDrawEnabled={false}
        hitGraphEnabled={true}
        imageSmoothingEnabled={true}
        // Prevent context menu and other unwanted interactions
        onContextMenu={(e) => e.evt.preventDefault()}
        // Add error boundary for stage interactions
        onError={(e) => {
          console.error('Konva Stage Error:', e);
        }}
      >
        <Layer
          // Optimize layer performance
          listening={true}
          perfectDrawEnabled={false}
          hitGraphEnabled={true}
        >
          {side === "front" && (
            <>
              {/* Dynamic background fill - only needed when image doesn't fill canvas exactly */}
              {!fitToImage && (
                <KonvaRect
                  x={0}
                  y={0}
                  width={effectiveCardWidth}
                  height={effectiveCardHeight}
                  fill={edgeColor || '#f5f5f5'}
                  draggable={false}
                  listening={false}
                />
              )}
              {bg && !imageError ? (
                <KonvaImage
                  image={bg}
                  width={w * imgScale}
                  height={h * imgScale}
                  x={imgX}
                  y={imgY}
                  // Prevent image from being draggable
                  draggable={false}
                  listening={false}
                />
              ) : (
                // Fallback rectangle when image fails to load
                <KonvaRect
                  x={imgX}
                  y={imgY}
                  width={w * imgScale}
                  height={h * imgScale}
                  fill="#f3f4f6"
                  stroke="#d1d5db"
                  strokeWidth={2}
                  cornerRadius={8}
                  draggable={false}
                  listening={false}
                />
              )}
              
              {/* Cover image frame/border - drawn at canvas edges */}
              {coverBorderWidth > 0 && coverBorderColor && bg && (
                <>
                  {/* SOLID frame style */}
                  {coverBorderStyle === 'solid' && (
                    <KonvaRect
                      x={scaledCoverBorderWidth / 2}
                      y={scaledCoverBorderWidth / 2}
                      width={effectiveCardWidth - scaledCoverBorderWidth}
                      height={effectiveCardHeight - scaledCoverBorderWidth}
                      stroke={coverBorderColor}
                      strokeWidth={scaledCoverBorderWidth}
                      fill="transparent"
                      draggable={false}
                      listening={false}
                    />
                  )}
                  
                  {/* DOUBLE frame style */}
                  {coverBorderStyle === 'double' && (
                    <>
                      <KonvaRect
                        x={scaledCoverBorderWidth * 0.15}
                        y={scaledCoverBorderWidth * 0.15}
                        width={effectiveCardWidth - scaledCoverBorderWidth * 0.3}
                        height={effectiveCardHeight - scaledCoverBorderWidth * 0.3}
                        stroke={coverBorderColor}
                        strokeWidth={Math.max(2, scaledCoverBorderWidth * 0.25)}
                        fill="transparent"
                        draggable={false}
                        listening={false}
                      />
                      <KonvaRect
                        x={scaledCoverBorderWidth * 0.6}
                        y={scaledCoverBorderWidth * 0.6}
                        width={effectiveCardWidth - scaledCoverBorderWidth * 1.2}
                        height={effectiveCardHeight - scaledCoverBorderWidth * 1.2}
                        stroke={coverBorderColor}
                        strokeWidth={Math.max(2, scaledCoverBorderWidth * 0.25)}
                        fill="transparent"
                        draggable={false}
                        listening={false}
                      />
                    </>
                  )}
                  
                  {/* DASHED frame style */}
                  {coverBorderStyle === 'dashed' && (
                    <KonvaRect
                      x={scaledCoverBorderWidth / 2}
                      y={scaledCoverBorderWidth / 2}
                      width={effectiveCardWidth - scaledCoverBorderWidth}
                      height={effectiveCardHeight - scaledCoverBorderWidth}
                      stroke={coverBorderColor}
                      strokeWidth={scaledCoverBorderWidth}
                      dash={[scaledCoverBorderWidth * 1.5, scaledCoverBorderWidth * 0.75]}
                      fill="transparent"
                      draggable={false}
                      listening={false}
                    />
                  )}
                  
                </>
              )}
              
              {/* Decorative overlay - scales proportionally with template size */}
              {coverOverlay !== 'none' && bg && (
                <DecorativeOverlay
                  type={coverOverlay}
                  color={coverOverlayColor}
                  imgX={imgX}
                  imgY={imgY}
                  imgW={w * imgScale}
                  imgH={h * imgScale}
                  scale={proportionalScale}
                />
              )}
            </>
          )}

          {side !== "front" && (
            <>
              {/* Inside card - white background first */}
              <KonvaRect
                x={0}
                y={0}
                width={effectiveCardWidth}
                height={effectiveCardHeight}
                fill="#ffffff"
                draggable={false}
                listening={false}
              />
              
              {/* Border rendering based on style */}
              {insideBorderWidth > 0 && insideBorderColor && (
                <>
                  {/* SOLID border style */}
                  {insideBorderStyle === 'solid' && (
                    <>
                      <KonvaRect
                        x={0}
                        y={0}
                        width={effectiveCardWidth}
                        height={effectiveCardHeight}
                        stroke={insideBorderColor}
                        strokeWidth={insideBorderWidth}
                        fill="transparent"
                        draggable={false}
                        listening={false}
                      />
                      {/* Inner decorative line */}
                      {insideBorderWidth > 8 && (
                        <KonvaRect
                          x={insideBorderWidth + 4}
                          y={insideBorderWidth + 4}
                          width={effectiveCardWidth - (insideBorderWidth * 2) - 8}
                          height={effectiveCardHeight - (insideBorderWidth * 2) - 8}
                          stroke={insideBorderColor}
                          strokeWidth={1}
                          fill="transparent"
                          opacity={0.4}
                          draggable={false}
                          listening={false}
                        />
                      )}
                    </>
                  )}
                  
                  {/* DOUBLE border style */}
                  {insideBorderStyle === 'double' && (
                    <>
                      <KonvaRect
                        x={0}
                        y={0}
                        width={effectiveCardWidth}
                        height={effectiveCardHeight}
                        stroke={insideBorderColor}
                        strokeWidth={Math.max(3, insideBorderWidth * 0.25)}
                        fill="transparent"
                        draggable={false}
                        listening={false}
                      />
                      <KonvaRect
                        x={insideBorderWidth * 0.6}
                        y={insideBorderWidth * 0.6}
                        width={effectiveCardWidth - insideBorderWidth * 1.2}
                        height={effectiveCardHeight - insideBorderWidth * 1.2}
                        stroke={insideBorderColor}
                        strokeWidth={Math.max(3, insideBorderWidth * 0.25)}
                        fill="transparent"
                        draggable={false}
                        listening={false}
                      />
                    </>
                  )}
                  
                  {/* DASHED border style */}
                  {insideBorderStyle === 'dashed' && (
                    <>
                      <KonvaRect
                        x={insideBorderWidth / 2}
                        y={insideBorderWidth / 2}
                        width={effectiveCardWidth - insideBorderWidth}
                        height={effectiveCardHeight - insideBorderWidth}
                        stroke={insideBorderColor}
                        strokeWidth={insideBorderWidth}
                        dash={[insideBorderWidth * 1.5, insideBorderWidth * 0.75]}
                        fill="transparent"
                        draggable={false}
                        listening={false}
                      />
                    </>
                  )}
                  
                </>
              )}
            </>
          )}

          {/* Render text based on mode */}
          {side === "front" ? (
            // Cover/front: single text
            <EditableText
              textProps={safeTextProps}
              onChange={setText}
              bounds={bounds}
              imageBounds={bg ? {
                x: imgX,
                y: imgY,
                width: w * imgScale,
                height: h * imgScale
              } : null}
            />
          ) : (
            // Inside: show owner's text AND collaborator entries
            <>
              {/* Owner's text (main textProps) - always show if there's text */}
              {safeTextProps.text && safeTextProps.text.trim() && (
                <EditableText
                  textProps={safeTextProps}
                  onChange={setText}
                  bounds={bounds}
                  imageBounds={null}
                />
              )}
              
              {/* Collaborator text entries - each with their own signature */}
              {insideTextEntries && insideTextEntries.length > 0 && (
                insideTextEntries.map((entry) => {
                  const entryFontSize = entry.fontSize || safeTextProps.fontSize || 24;
                  const entryFontFamily = entry.fontFamily || safeTextProps.fontFamily || "'Inter', sans-serif";
                  const entryFill = entry.fill || safeTextProps.fill || "#000000";
                  const entryX = entry.x ?? bounds.x;
                  const entryY = entry.y ?? bounds.y;
                  const entryWidth = entry.width ?? bounds.width;
                  
                  // Estimate text height for signature positioning
                  // Rough estimate: fontSize * lineHeight * number of lines
                  const lineHeight = 1.4;
                  const avgCharWidth = entryFontSize * 0.5;
                  const charsPerLine = Math.max(1, Math.floor(entryWidth / avgCharWidth));
                  const estimatedLines = Math.max(1, Math.ceil((entry.text || "").length / charsPerLine));
                  const textHeight = estimatedLines * entryFontSize * lineHeight;
                  
                  return (
                    <React.Fragment key={entry.id}>
                      <EditableText
                        textProps={{
                          x: entryX,
                          y: entryY,
                          width: entryWidth,
                          text: entry.text || "",
                          fontSize: entryFontSize,
                          fontFamily: entryFontFamily,
                          fill: entryFill,
                          align: entry.align || "left",
                          wrap: "word",
                          draggable: entry.draggable !== false,
                          rotation: entry.rotation || 0,
                        }}
                        onChange={(updatedProps) => {
                          if (onTextEntryChange) {
                            onTextEntryChange(entry.id, updatedProps);
                          }
                        }}
                        bounds={bounds}
                        imageBounds={null}
                      />
                      {/* Render signature below this entry's text */}
                      {entry.signature && (
                        <KonvaText
                          x={entryX}
                          y={entryY + textHeight + 8}
                          width={entryWidth}
                          text={`— ${entry.signature}`}
                          fontSize={Math.max(12, entryFontSize * 0.75)}
                          fontFamily={entryFontFamily}
                          fontStyle="italic"
                          fill={entryFill}
                          opacity={0.8}
                          align={entry.align || "left"}
                          listening={false}
                          draggable={false}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </>
          )}
        </Layer>
      </Stage>
    </div>
  );
}
